import { ApiError } from '@/api/http/types'
import type {
  InstrumentInfoEntryViewState,
  ProviderInstrumentInfo,
  ProviderInstrumentStatus,
} from './types'

/** Draft / missing instruments return 404 `not_found` from campaign `…/info`. */
export function isInstrumentNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.isNotFound
}

/** Response MS blocks status / save / poll when instrument status is `paused` or `stopped`. */
/** BFP-1142 — provider dropped from the audience after a live survey was republished. */
export function isInstrumentNotInvitedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === 'forbidden'
}

export function isInstrumentPausedError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === 'instrument_paused' || error.code === 'instrument_unavailable')
  )
}

/** Full-page error only when Instrument Info (`…/info`) fails — not when status fails. */
export function isInstrumentInfoLoadFailed(input: {
  info: ProviderInstrumentInfo | undefined
  infoLoading: boolean
  infoError: boolean
}): boolean {
  if (input.infoLoading) return false
  return input.infoError || !input.info
}

export function deriveInstrumentInfoViewState(input: {
  info: ProviderInstrumentInfo
  status: ProviderInstrumentStatus | undefined
}): InstrumentInfoEntryViewState {
  if (input.status?.alreadyCompleted) return 'alreadyCompleted'
  const hasActiveSession = Boolean(
    input.status?.activeResponseId ?? input.status?.resumableResponseId,
  )
  if (input.status?.capacityReached && !hasActiveSession) return 'capacityReached'
  if (input.info.status === 'paused' || input.info.status === 'stopped') return 'paused'
  // Terminal: completed (target), expired (timeline), closed (manual / legacy)
  if (
    input.info.status === 'completed' ||
    input.info.status === 'expired' ||
    input.info.status === 'closed'
  ) {
    return 'closed'
  }
  return 'ready'
}

export function showStartSurveyCta(input: {
  viewState: InstrumentInfoEntryViewState
  status: ProviderInstrumentStatus | undefined
}): boolean {
  if (input.viewState !== 'ready') return false
  const activeId = input.status?.activeResponseId ?? input.status?.resumableResponseId
  return !activeId
}

export function showContinueSurveyCta(input: {
  status: ProviderInstrumentStatus | undefined
  viewState: InstrumentInfoEntryViewState
}): boolean {
  if (input.viewState !== 'ready') return false
  const activeId = input.status?.activeResponseId ?? input.status?.resumableResponseId
  return Boolean(activeId)
}

export type SurveyAvailabilityBadge = 'available' | 'unavailable'

/** Blue “Survey available” only when status loaded and instrument is startable. */
export function deriveSurveyAvailabilityBadge(input: {
  info: ProviderInstrumentInfo
  status: ProviderInstrumentStatus | undefined
  statusError: boolean
  statusLoading: boolean
}): SurveyAvailabilityBadge {
  if (input.statusError) {
    return 'unavailable'
  }
  if (input.statusLoading) {
    return 'available'
  }
  if (!input.status) {
    return 'unavailable'
  }
  if (input.info.status !== 'active') {
    return 'unavailable'
  }
  const viewState = deriveInstrumentInfoViewState({
    info: input.info,
    status: input.status,
  })
  return viewState === 'ready' ? 'available' : 'unavailable'
}
